import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Link, useSearchParams } from "react-router";
import { listSharks } from "@/api/client";
import type { SharkSort, SharkSummary } from "@/api/types";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatCount, yearOf } from "@/lib/format";
import { RARITIES } from "@/lib/rarity";
import { RarityMark, SpecimenCard, count, to, useStats } from "@/site/parts";
import { SpotGlyph } from "@/site/plate";
import {
  SORTS,
  VIEWS,
  parseRegistryState,
  withRegistryState,
  type RegistryState,
} from "@/site/registry-state";

const PAGE_SIZE = 48;

// ------------------------------------------------------------------ data

interface Listing {
  run: string;
  items: SharkSummary[];
  total: number | null;
  /** `loading`: a new search or sort is on its way; the previous items stay on screen. */
  status: "loading" | "idle" | "more" | "error";
  error: Error | null;
}

const asError = (e: unknown) => (e instanceof Error ? e : new Error(String(e)));

/**
 * The last settled listing per sort and search, for this visit. Coming back to the registry (a
 * specimen, then Back) renders every loaded page at once, so the scroll position can be restored.
 */
const settled = new Map<string, Listing>();

function useListing(sort: SharkSort, q: string) {
  const key = `${sort}:${q}`;
  const [attempt, setAttempt] = useState(0);
  const run = `${attempt}:${key}`;
  const [restored] = useState(() => (settled.has(key) ? run : null));
  const [state, setState] = useState<Listing>(() => {
    const cached = settled.get(key);
    return cached
      ? { ...cached, run }
      : { run, items: [], total: null, status: "loading", error: null };
  });

  useEffect(() => {
    if (state.status !== "idle") return;
    settled.set(state.run.slice(state.run.indexOf(":") + 1), { ...state, error: null });
  }, [state]);

  useEffect(() => {
    if (run === restored) return;
    const controller = new AbortController();
    listSharks({ sort, q: q || undefined, offset: 0, limit: PAGE_SIZE }, controller.signal).then(
      (page) =>
        setState({ run, items: page.items, total: page.total, status: "idle", error: null }),
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ run, items: [], total: null, status: "error", error: asError(error) });
      },
    );
    return () => controller.abort();
  }, [run, restored, sort, q]);

  async function loadMore() {
    const offset = state.items.length;
    setState((s) => ({ ...s, status: "more", error: null }));
    try {
      const page = await listSharks({ sort, q: q || undefined, offset, limit: PAGE_SIZE });
      setState((s) => {
        if (s.run !== run) return s;
        // The registry can change between pages and shift rows; never list a shark twice.
        const seen = new Set(s.items.map((item) => item.id));
        const fresh = page.items.filter((item) => !seen.has(item.id));
        return { ...s, items: [...s.items, ...fresh], total: page.total, status: "idle" };
      });
    } catch (error) {
      setState((s) => (s.run === run ? { ...s, status: "idle", error: asError(error) } : s));
    }
  }

  const current: Listing = state.run === run ? state : { ...state, status: "loading", error: null };
  return { listing: current, loadMore, reload: () => setAttempt((n) => n + 1) };
}

// ------------------------------------------------------------------ controls

function SearchField({ q, onSearch }: { q: string; onSearch: (q: string) => void }) {
  const id = useId();
  const [draft, setDraft] = useState(q);
  const [synced, setSynced] = useState(q);
  // What this field last sent: a URL change that matches it is our own echo, arriving while the
  // user may already have typed on, so it must not overwrite the draft.
  const [sent, setSent] = useState(q);
  // Follow the URL when it changes underneath the field (back / forward, a link).
  if (q !== synced) {
    setSynced(q);
    if (q !== sent.trim() && draft.trim() !== q) setDraft(q);
  }
  // The debounce restarts on typing only, not whenever the caller passes a new closure.
  const searchRef = useRef(onSearch);
  useLayoutEffect(() => {
    searchRef.current = onSearch;
  });
  const send = useCallback((value: string) => {
    setSent(value);
    searchRef.current(value);
  }, []);
  useEffect(() => {
    if (draft.trim() === q) return;
    const timer = window.setTimeout(() => send(draft), 250);
    return () => window.clearTimeout(timer);
  }, [draft, q, send]);

  function submit(event: FormEvent) {
    event.preventDefault();
    send(draft);
  }

  return (
    <form role="search" onSubmit={submit} className="reg-search">
      <label className="field" htmlFor={`${id}-q`}>
        <span className="label">Search · name, catalogue no., alias, region</span>
        <input
          id={`${id}-q`}
          type="search"
          className="input"
          value={draft}
          placeholder="RT-00042, TZ-010, Tanzania…"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setDraft(e.target.value)}
        />
      </label>
    </form>
  );
}

function Segmented<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
}: {
  legend: string;
  name: string;
  value: T;
  options: readonly { value: T; label: string; hint?: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="seg">
      <legend className="label">{legend}</legend>
      <div className="seg-row">
        {options.map((o) => (
          <label key={o.value} title={o.hint}>
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// ------------------------------------------------------------------ results

function Thumb({ shark }: { shark: SharkSummary }) {
  if (shark.thumb_url) {
    return <img src={shark.thumb_url} alt="" className="reg-thumb" loading="lazy" />;
  }
  if (shark.constellation) {
    return (
      <div className="reg-thumb">
        <SpotGlyph
          constellation={shark.constellation}
          label="Hand-marked spots, no photo on file"
          dot={0.028}
        />
      </div>
    );
  }
  return <span className="reg-thumb" aria-hidden="true" />;
}

function SpecimenTable({ items }: { items: SharkSummary[] }) {
  return (
    <div className="scroll-x">
      <table className="table reg-table">
        <thead>
          <tr>
            <th scope="col" className="r">
              No.
            </th>
            <th scope="col">
              <span className="sr-only">Photograph or spots</span>
            </th>
            <th scope="col">Specimen</th>
            <th scope="col">Rarity</th>
            <th scope="col" className="r">
              Enc
            </th>
            <th scope="col" className="hide-sm">
              First
            </th>
            <th scope="col" className="hide-sm">
              Last
            </th>
            <th scope="col" className="hide-md">
              Region
            </th>
            <th scope="col" className="hide-md">
              Aliases
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((s, i) => (
            <tr key={s.id}>
              <td className="r mono faint" style={{ fontSize: 12 }}>
                {i + 1}
              </td>
              <td style={{ width: 76 }}>
                <Thumb shark={s} />
              </td>
              <td>
                <Link to={to.shark(s.id)} className="link-u" style={{ fontWeight: 500 }}>
                  {s.display_name}
                </Link>
                <span className="label" style={{ display: "block", marginTop: 2 }}>
                  {s.id}
                  {s.thumb_url ? "" : " · spots only"}
                </span>
              </td>
              <td>
                <RarityMark rarity={s.rarity} />
              </td>
              <td className="r mono">{s.encounters}</td>
              <td className="mono hide-sm">{yearOf(s.first_seen) ?? "—"}</td>
              <td className="mono hide-sm">{yearOf(s.last_seen) ?? "—"}</td>
              <td className="hide-md">{s.regions.join(" · ") || "—"}</td>
              <td className="label hide-md">
                {s.sources.map((a) => a.source_id.slice(0, 18)).join(" · ") || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ q, onClear }: { q: string; onClear: () => void }) {
  return (
    <div className="reg-empty">
      <p className="label label-signal">0 results</p>
      <p className="h-3" style={{ marginTop: 10 }}>
        No individual answers to &ldquo;{q}&rdquo;.
      </p>
      <p className="muted" style={{ marginTop: 8, maxWidth: "36em" }}>
        Try a catalogue number (RT-00042), a Sharkbook alias (TZ-010) or a place (Ningaloo). Unnamed
        sharks go by an epithet that ends in their region.
      </p>
      <button type="button" className="btn btn-ghost" style={{ marginTop: 20 }} onClick={onClear}>
        Clear the search <span className="btn-arrow">×</span>
      </button>
    </div>
  );
}

function Status({ state, listing }: { state: RegistryState; listing: Listing }) {
  const sort = SORTS.find((s) => s.value === state.sort)!;
  const total = listing.total;
  let text: string;
  if (listing.status === "loading") {
    text = state.q ? `Searching for “${state.q}”…` : "Retrieving the registry";
  } else if (state.q) {
    text = `${count(total)} ${total === 1 ? "match" : "matches"} for “${state.q}”`;
  } else text = `${count(total)} individuals`;
  return (
    <p className="reg-status label" aria-live="polite">
      <span>
        <span
          className={`status-dot${listing.status === "loading" ? " blink" : ""}`}
          data-tone={listing.status === "error" ? "off" : undefined}
        />
        <span style={{ color: "var(--ink)" }}>{text}</span>
      </span>
      <span>
        Sorted by {sort.label.toLowerCase()} · {sort.hint.toLowerCase()}
      </span>
    </p>
  );
}

function More({ listing, onMore }: { listing: Listing; onMore: () => Promise<void> }) {
  const countRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const shown = listing.items.length;
  const total = listing.total ?? 0;
  if (total === 0 || listing.status === "loading") return null;
  const remaining = total - shown;
  const busy = listing.status === "more";

  // aria-disabled, not disabled: a disabled button drops keyboard focus to <body>.
  async function more() {
    if (busy) return;
    await onMore();
    // The last page removes the button; hand focus to the count rather than losing it.
    requestAnimationFrame(() => {
      if (!buttonRef.current?.isConnected) countRef.current?.focus();
    });
  }

  return (
    <div className="reg-more">
      <div className="reg-progress" aria-hidden="true">
        <span style={{ width: `${(shown / total) * 100}%` }} />
      </div>
      <p className="label" ref={countRef} tabIndex={-1}>
        Showing <b className="mono">{formatCount(shown)}</b> of {formatCount(total)}
      </p>
      {listing.error && (
        <p role="alert" className="alert">
          The next page did not load: {listing.error.message}
        </p>
      )}
      {remaining > 0 && (
        <button
          ref={buttonRef}
          type="button"
          className="btn"
          onClick={() => void more()}
          aria-disabled={busy}
        >
          {busy ? "Retrieving…" : `Load ${formatCount(Math.min(remaining, PAGE_SIZE))} more`}
          <span className="btn-arrow" aria-hidden="true">
            ↓
          </span>
        </button>
      )}
    </div>
  );
}

function RarityKey() {
  return (
    <dl className="kv" aria-label="Rarity, by sightings on record">
      {RARITIES.map((r) => (
        <div key={r.rarity}>
          <dt>
            <RarityMark rarity={r.rarity} />
          </dt>
          <dd>{r.blurb}</dd>
        </div>
      ))}
    </dl>
  );
}

// ------------------------------------------------------------------ page

export function RegistryPage() {
  const [params, setParams] = useSearchParams();
  const state = parseRegistryState(params);
  const stats = useStats();
  const { listing, loadMore, reload } = useListing(state.sort, state.q);
  useDocumentMeta({
    title: state.q ? `“${state.q}” in the registry` : "Registry",
    description: "Every whale shark in the Gillspotter registry: search, sort and browse.",
  });

  const update = (patch: Partial<RegistryState>) =>
    setParams((current) => withRegistryState(current, patch), { replace: true });

  const stale = listing.status === "loading" && listing.items.length > 0;
  return (
    <>
      <section className="in-wrap in-grid" style={{ paddingTop: 48, rowGap: 32 }}>
        <div style={{ gridColumn: "span 7" }} className="anim-in">
          <p className="label">
            <span className="status-dot" /> Registry · Rhincodon typus
          </p>
          <h1 className="display-2" style={{ marginTop: 14 }}>
            {count(stats?.sharks)} individuals, each known by its spots.
          </h1>
          <p className="lede" style={{ marginTop: 20 }}>
            Every shark on record, as a specimen: catalogue number, rarity by sightings, and the
            photograph or hand-marked constellation that identifies it. Sharks from the
            whaleshark.org export have no photograph here (it stays on Sharkbook), so they are shown
            by their spots.
          </p>
        </div>
        <div style={{ gridColumn: "9 / span 4", alignSelf: "end" }} className="anim-in">
          <RarityKey />
        </div>
      </section>

      <section className="in-wrap" style={{ paddingTop: 48 }} aria-labelledby="registry-results">
        <h2 id="registry-results" className="sr-only">
          Specimens
        </h2>
        <div className="reg-bar">
          <SearchField q={state.q} onSearch={(q) => update({ q })} />
          <Segmented
            legend="Sort"
            name="sort"
            value={state.sort}
            options={SORTS}
            onChange={(sort) => update({ sort })}
          />
          <Segmented
            legend="View"
            name="view"
            value={state.view}
            options={VIEWS}
            onChange={(view) => update({ view })}
          />
        </div>
        <Status state={state} listing={listing} />

        {listing.status === "error" ? (
          <div role="alert" style={{ paddingTop: 32, display: "grid", gap: 16, maxWidth: 640 }}>
            <p className="alert">The registry did not load: {listing.error?.message}</p>
            <button type="button" className="btn" style={{ justifySelf: "start" }} onClick={reload}>
              Retry <span className="btn-arrow">↻</span>
            </button>
          </div>
        ) : listing.status !== "loading" && listing.items.length === 0 ? (
          <Empty q={state.q} onClear={() => update({ q: "" })} />
        ) : (
          <div className="reg-results" aria-busy={listing.status === "loading"} data-stale={stale}>
            {listing.items.length === 0 && <div className="bar-indeterminate" />}
            {state.view === "table" ? (
              <SpecimenTable items={listing.items} />
            ) : (
              <div className="spec-grid">
                {listing.items.map((s, i) => (
                  <SpecimenCard
                    key={s.id}
                    shark={s}
                    index={i}
                    delay={Math.min(i % PAGE_SIZE, 12)}
                  />
                ))}
              </div>
            )}
            <More listing={listing} onMore={loadMore} />
          </div>
        )}
      </section>
    </>
  );
}
